/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Product: Cuenta → Confianza + apelación E5.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { ShellScrollScreen } from '../../shared/layout/ShellScrollScreen';
import { spacing } from '../../shared/theme/spacing';
import { ensureTicketFonts } from '../../shared/ticket/ensureTicketFonts'
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import { navigateInShell } from '../../shell/navigation/rootNavigation';
import {
  fetchMyOrganizationTrustStatus,
  formatMerchantTrustReason,
  trustStatusBody,
  trustStatusTitle,
  type OrgTrustStatus,
} from '../infra/orgTrustStatusService';
import {
  APPEAL_REASON_LABELS,
  fetchOrgTrustAppealSurface,
  submitOrgTrustAppeal,
  type AppealReasonCode,
  type OrgTrustAppealSurface,
} from '../infra/orgTrustAppealService';

const REASONS = Object.keys(APPEAL_REASON_LABELS) as AppealReasonCode[];

function formatUntil(iso: string | null): string | null {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return null;
  }
}

export function MarketplaceTrustScreen() {
  const navigation = useNavigation<any>();
  const [trust, setTrust] = useState<OrgTrustStatus | null>(null);
  const [appealSurface, setAppealSurface] = useState<OrgTrustAppealSurface | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reason, setReason] = useState<AppealReasonCode>('evidence_error');
  const [statement, setStatement] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formOk, setFormOk] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const [t, a] = await Promise.all([
        fetchMyOrganizationTrustStatus({ force: true }),
        fetchOrgTrustAppealSurface(),
      ]);
      setTrust(t);
      setAppealSurface(a);
    } catch {
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    ensureTicketFonts();
    void load();
  }, [load]);

  const status = trust?.status ?? 'active';
  const until = formatUntil(trust?.statusUntil ?? null);
  const reasonLabel = formatMerchantTrustReason(trust?.statusReason);
  const openAppeal = appealSurface?.open_appeal ?? null;
  const canAppeal = Boolean(appealSurface?.appealable && appealSurface.target_event?.id);

  const onSubmitAppeal = async () => {
    if (!appealSurface?.target_event?.id) return;
    if (statement.trim().length < 20) {
      setFormError('Escribí al menos 20 caracteres.');
      return;
    }
    setSubmitting(true);
    setFormError(null);
    setFormOk(null);
    const res = await submitOrgTrustAppeal({
      enforcementEventId: appealSurface.target_event.id,
      reasonCode: reason,
      statement: statement.trim(),
    });
    setSubmitting(false);
    if (!res.ok) {
      setFormError(res.message);
      return;
    }
    setFormOk('Apelación enviada. La medida sigue hasta que la revisemos.');
    setStatement('');
    await load();
  };

  return (
    <ShellScrollScreen refreshing={loading} onRefresh={() => void load()}>
      <View style={styles.card}>
        <Text style={styles.kicker}>Tu organización</Text>
        {loadFailed && !loading ? (
          <>
            <Text style={styles.title}>No se pudo verificar tu estado</Text>
            <Text style={styles.body}>
              No pudimos cargar tu estado de confianza.
            </Text>
            <TouchableOpacity
              style={styles.ctaGhost}
              onPress={() => void load()}
              accessibilityRole="button"
              accessibilityLabel="Reintentar carga del estado de confianza"
            >
              <Text style={styles.ctaGhostText}>Reintentar</Text>
            </TouchableOpacity>
          </>
        ) : (
          <>
            <Text style={styles.title}>{trustStatusTitle(status)}</Text>
            <Text style={styles.body}>{trustStatusBody(status)}</Text>

            {reasonLabel ? <Text style={styles.meta}>Motivo: {reasonLabel}</Text> : null}
            {until && (status === 'restricted' || status === 'suspended') ? (
              <Text style={styles.meta}>Hasta: {until}</Text>
            ) : null}
            {typeof trust?.strikesActive === 'number' && trust.strikesActive > 0 ? (
              <Text style={styles.meta}>Avisos activos: {trust.strikesActive}</Text>
            ) : null}
          </>
        )}
      </View>

      <View style={styles.card}>
        <Text style={styles.sectionTitle}>Qué podés hacer</Text>
        <Text style={styles.body}>
          {status === 'active'
            ? 'Nada pendiente. El POS y la tienda online andan bien.'
            : status === 'warned'
              ? 'Seguí vendiendo. Si el aviso se repite, puede haber una restricción.'
              : 'El mostrador sigue. Podés apelar si estás en plazo.'}
        </Text>

        {openAppeal ? (
          <>
            <Text style={styles.sectionTitle}>Apelación en curso</Text>
            <Text style={styles.body}>{openAppeal.statement}</Text>
            <Text style={styles.meta}>
              {openAppeal.staff_public_reply
                ? `Respuesta: ${openAppeal.staff_public_reply}`
                : 'Lo estamos revisando. La medida sigue.'}
            </Text>
          </>
        ) : null}

        {canAppeal ? (
          <>
            <Text style={styles.sectionTitle}>Apelar</Text>
            <Text style={styles.meta}>
              Plazo: {appealSurface?.target_event?.window_days ?? 7} días. Una por medida.
            </Text>
            <View style={styles.reasonRow}>
              {REASONS.map((r) => (
                <TouchableOpacity
                  key={r}
                  style={[styles.chip, reason === r && styles.chipOn]}
                  onPress={() => setReason(r)}
                >
                  <Text style={[styles.chipText, reason === r && styles.chipTextOn]}>
                    {APPEAL_REASON_LABELS[r]}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
            <TextInput
              style={styles.input}
              multiline
              value={statement}
              onChangeText={setStatement}
              placeholder="Contanos qué pasó (mín. 20 caracteres)"
              placeholderTextColor={ticketColors.ink3}
            />
            {formError ? <Text style={styles.err}>{formError}</Text> : null}
            {formOk ? <Text style={styles.ok}>{formOk}</Text> : null}
            <TouchableOpacity
              style={styles.cta}
              disabled={submitting}
              onPress={() => void onSubmitAppeal()}
            >
              <Text style={styles.ctaText}>{submitting ? 'Enviando…' : 'Enviar apelación'}</Text>
            </TouchableOpacity>
          </>
        ) : null}

        <TouchableOpacity
          style={styles.ctaGhost}
          onPress={() => navigateInShell(navigation, 'SupportReports')}
        >
          <Text style={styles.ctaGhostText}>Ir a soporte</Text>
        </TouchableOpacity>
      </View>
    </ShellScrollScreen>
  );
}

const styles = StyleSheet.create({
  card: {
    marginHorizontal: spacing.md,
    marginTop: spacing.md,
    padding: spacing.md,
    borderRadius: 10,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    gap: spacing.sm,
  },
  kicker: {
    fontFamily: ticketFonts.sans,
    fontSize: 12,
    color: ticketColors.ink3,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  title: {
    fontFamily: ticketFonts.sans,
    fontSize: 22,
    color: ticketColors.ink,
  },
  sectionTitle: {
    fontFamily: ticketFonts.sans,
    fontSize: 16,
    color: ticketColors.ink,
    marginTop: spacing.xs,
  },
  body: {
    fontFamily: ticketFonts.sans,
    fontSize: 14,
    lineHeight: 20,
    color: ticketColors.ink2,
  },
  meta: {
    fontFamily: ticketFonts.sans,
    fontSize: 13,
    color: ticketColors.ink3,
  },
  reasonRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  chip: {
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  chipOn: { borderColor: ticketColors.gold, backgroundColor: ticketColors.brandSoft },
  chipText: { fontSize: 12, color: ticketColors.ink2, fontFamily: ticketFonts.sans },
  chipTextOn: { color: ticketColors.ink, fontWeight: '700' },
  input: {
    minHeight: 88,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    padding: spacing.sm,
    textAlignVertical: 'top',
    fontFamily: ticketFonts.sans,
    fontSize: 14,
    color: ticketColors.ink,
  },
  err: { color: '#B45309', fontSize: 13, fontFamily: ticketFonts.sans },
  ok: { color: ticketColors.gold, fontSize: 13, fontFamily: ticketFonts.sans },
  cta: {
    alignSelf: 'flex-start',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: 8,
    backgroundColor: ticketColors.gold,
  },
  ctaText: {
    fontFamily: ticketFonts.sans,
    fontSize: 14,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  ctaGhost: {
    alignSelf: 'flex-start',
    paddingVertical: spacing.sm,
  },
  ctaGhostText: {
    fontFamily: ticketFonts.sans,
    fontSize: 14,
    color: ticketColors.gold,
    fontWeight: '600',
  },
});
